import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";
import { fetchRenderConfig } from "../api/renderApi";
import type { RenderResponse } from "../types/banner";

type RenderContextType = {
    data: RenderResponse | null;
    loading: boolean;
    error: any;
    refetch: () => Promise<void>;
};

const RenderContext = createContext<RenderContextType>({
    data: null,
    loading: true,
    error: null,
    refetch: async () => { },
});

export const RenderProvider = ({ children }: { children: ReactNode }) => {
    const [data, setData] = useState<RenderResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<any>(null);

    useEffect(() => {
        const controller = new AbortController();

        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchRenderConfig(controller.signal);
                setData(res);
                setError(null);
            } catch (err) {
                if ((controller as any).signal?.aborted) return;
                setError(err);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        load();

        return () => controller.abort();
    }, []);

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchRenderConfig();
            setData(res);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    }, []);

    return (
        <RenderContext.Provider value={{ data, loading, error, refetch }}>
            {children}
        </RenderContext.Provider>
    );
};

export const useRender = () => useContext(RenderContext);

export default RenderContext;
